/**
 * Telemetry bootstrap — call ONCE, from `src/index.tsx`, before the app mounts.
 *
 * Order matters:
 *  1. Bind the unload hooks FIRST. A page can die during boot (a stuck splash
 *     that the user kills is a real, measured failure — CHANGELOG [2.4.12]),
 *     and without the hooks bound that session leaves nothing behind.
 *  2. Emit the `app.pageload` anchor row.
 *  3. Enter the `boot` phase, so a session that never gets past the splash still
 *     produces a `phase.enter` — the denominator that makes "stuck on splash"
 *     a rate rather than an anecdote.
 *  4. Drain the outbox last, and asynchronously: those rows are from a PREVIOUS
 *     page load and must never delay this one.
 */

import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { collection, db } from "@/Utils/firebase";
import { logEvent } from "@/Utils/logger";
import { Events, Phases } from "./events";
import { bindOutboxFlush, takeOutbox, type PendingRow } from "./outbox";
import { bindPhaseLifecycle, enterPhase } from "./phases";
import { logPageLoad } from "./pageload";
import { bindAlignmentAccumulator } from "./alignmentAccumulator";
import { bindRotationAccumulator } from "./rotationAccumulator";

/**
 * Re-send rows the previous page load could not confirm.
 *
 * Uses the row's ORIGINAL client-generated id with `setDoc`, so a row that
 * actually landed before the page died is overwritten rather than duplicated.
 *
 * `serverTs` is necessarily re-stamped to the drain time — that is unavoidable
 * and harmless, because a journey is ordered by `(journeyPageLoad, seq)`, both
 * of which are preserved from the original emit. `data.outboxDrained` marks the
 * row so an investigator can see that its `serverTs` is not its emit time.
 */
async function drainOutbox(): Promise<void> {
  let rows: PendingRow[];
  try {
    rows = takeOutbox();
  } catch {
    return;
  }
  if (!rows.length) return;

  let recovered = 0;
  for (const entry of rows) {
    try {
      const row = entry.row as Record<string, unknown>;
      const existingData =
        row.data && typeof row.data === "object"
          ? (row.data as Record<string, unknown>)
          : {};
      await setDoc(doc(collection(db, "pwa_logs"), entry.id), {
        ...row,
        serverTs: serverTimestamp(),
        data: { ...existingData, outboxDrained: true },
      });
      recovered++;
    } catch {
      // A row that will not write is dropped rather than retried forever. It
      // has already been removed from the outbox by takeOutbox().
    }
  }

  if (recovered > 0) {
    logEvent(
      Events.APP_OUTBOX_DRAINED,
      "[telemetry] recovered rows from a previous page load",
      { data: { recovered, attempted: rows.length } }
    );
  }
}

export function initTelemetry(): void {
  try {
    bindOutboxFlush();
    bindPhaseLifecycle();
    bindAlignmentAccumulator();
    bindRotationAccumulator();
    logPageLoad();
    enterPhase(Phases.BOOT);
    void drainOutbox();
  } catch {
    /* telemetry must never break bootstrap */
  }
}
